import { HttpStatusCode, isAxiosError } from 'axios';
import React from 'react';
import { toast } from 'react-toastify';

import api from '@/services/apiSgft';

import { MappingState, PendingChangesState } from './useMappingState';

interface UseTrainingOperationsProps {
	pendingChanges: PendingChangesState;
	modifiedCheckboxes: MappingState;
	setModifiedCheckboxes: React.Dispatch<React.SetStateAction<MappingState>>;
	setPendingChanges: React.Dispatch<React.SetStateAction<PendingChangesState>>;
	setIsMatrixChanging: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useTrainingOperations = ({
	pendingChanges,
	modifiedCheckboxes,
	setModifiedCheckboxes,
	setPendingChanges,
	setIsMatrixChanging,
}: UseTrainingOperationsProps) => {
	const handleSendCombinedChanges = React.useCallback(async () => {
		setIsMatrixChanging(true);
		const previousMatrixMap = new Map(modifiedCheckboxes.matrix);
		const previousBlockMatrixMap = new Map(modifiedCheckboxes.blockMatrix);

		try {
			const combinedChanges = {
				matrix: pendingChanges.matrix,
				blockMatrix: pendingChanges.blockMatrix,
			};

			const response = await api.post(
				'/position-regulatory-training-link',
				combinedChanges,
			);

			if (response.status !== 201) {
				throw new Error('Network response was not ok');
			}

			toast.success(response.data.message, {
				theme: 'colored',
				toastId: 'success',
			});

			setPendingChanges({
				matrix: {},
				blockMatrix: {},
			});

			return true;
		} catch (error) {
			if (isAxiosError(error)) {
				if (
					error.response?.status === HttpStatusCode.BadRequest &&
					Array.isArray(error.response.data.message)
				) {
					const errors: {
						trainingId: number;
						positionId: number;
						message: string;
					}[] = error.response.data.message;

					const newMatrixMap = new Map(previousMatrixMap);

					for (const mappingError of errors) {
						const { positionId, trainingId, message } = mappingError;
						const key = `${positionId}-${trainingId}`;

						toast.error(`Erro ao salvar mapeamento: ${message}`, {
							theme: 'colored',
							toastId: `error-trainings-${key}`,
						});
					}

					setModifiedCheckboxes({
						matrix: newMatrixMap,
						blockMatrix: previousBlockMatrixMap,
					});
				} else {
					toast.error(
						`Não foi possível salvar o mapeamento: ${error.response?.data.message || 'Erro desconhecido'}`,
						{
							theme: 'colored',
							toastId: `error-trainings`,
						},
					);

					setModifiedCheckboxes({
						matrix: previousMatrixMap,
						blockMatrix: previousBlockMatrixMap,
					});
				}
			} else {
				toast.error('Erro ao carregar os dados', {
					theme: 'colored',
					toastId: `error-trainings`,
				});
			}

			return false;
		} finally {
			setIsMatrixChanging(false);
		}
	}, [
		setIsMatrixChanging,
		modifiedCheckboxes.matrix,
		modifiedCheckboxes.blockMatrix,
		pendingChanges.matrix,
		pendingChanges.blockMatrix,
		setPendingChanges,
		setModifiedCheckboxes,
	]);

	return { handleSendCombinedChanges };
};
